import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Section, Cell, Input, Button } from '@telegram-apps/telegram-ui'
import { motion, AnimatePresence } from 'framer-motion'
import { Brain } from 'lucide-react'

import { api } from '../../lib/api'
import type { Fact } from '../../lib/types'
import PageHeader from '../../components/PageHeader'
import EmptyState from '../../components/EmptyState'
import { SkeletonList } from '../../components/Skeleton'
import { hapticImpact } from '../../lib/telegram'

export default function DirectorFacts() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<Fact[]>({
    queryKey: ['facts'],
    queryFn: () => api.get<Fact[]>('/facts'),
  })
  const [adding, setAdding] = useState(false)
  const [newText, setNewText] = useState('')

  const create = useMutation({
    mutationFn: () => api.post<Fact>('/facts', { content: newText }),
    onSuccess: () => {
      setAdding(false)
      setNewText('')
      qc.invalidateQueries({ queryKey: ['facts'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/facts/${id}`),
    onMutate: async (id) => {
      hapticImpact('medium')
      await qc.cancelQueries({ queryKey: ['facts'] })
      const prev = qc.getQueryData<Fact[]>(['facts'])
      qc.setQueryData<Fact[]>(['facts'], (old) => old?.filter((f) => f.id !== id) ?? [])
      return { prev }
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(['facts'], ctx.prev)
    },
  })

  return (
    <>
      <PageHeader title={`Память${data ? ` (${data.length})` : ''}`} caption="Что я о Вас помню" right={
        !adding && (
          <Button size="s" mode="bezeled" onClick={() => setAdding(true)}>+ Факт</Button>
        )
      } />

      {adding && (
        <Section>
          <Input
            placeholder="Например: дочь Алёна, ДР 14 мая"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 8, padding: '8px 16px' }}>
            <Button size="m" stretched onClick={() => create.mutate()} disabled={!newText.trim() || create.isPending}>
              Сохранить
            </Button>
            <Button size="m" mode="plain" onClick={() => { setAdding(false); setNewText('') }}>
              Отмена
            </Button>
          </div>
        </Section>
      )}

      {isLoading ? (
        <SkeletonList count={4} />
      ) : !data || data.length === 0 ? (
        <EmptyState icon={Brain} title="Пусто" description="Расскажите боту что-то про себя — и оно появится здесь." />
      ) : (
        <Section>
          <AnimatePresence>
            {data.map((f) => (
              <motion.div
                key={f.id}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, x: 50 }}
              >
                <Cell
                  multiline
                  after={
                    <button
                      onClick={() => remove.mutate(f.id)}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        fontSize: 18, opacity: 0.5, padding: 8,
                      }}
                      aria-label="Удалить"
                    >
                      🗑
                    </button>
                  }
                >
                  {f.content}
                </Cell>
              </motion.div>
            ))}
          </AnimatePresence>
        </Section>
      )}
    </>
  )
}
